import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';

import { AppState } from '../../../../store/models/app-state';
import { Contact } from '../../../models/contact';
import { PoolTimelineItemChange } from '../models/pool-timeline-itemChange';

interface PoolTimelineChangesOwnProps {
    changes: PoolTimelineItemChange[];
}

interface PoolTimelineChangesReduxProps {
    contacts: Contact[];
}

type PoolTimelineChangesProps = PoolTimelineChangesReduxProps & PoolTimelineChangesOwnProps;

interface PoolTimelineChangesState {
    sort: { key: string; order: 'asc' | 'desc' };
}

class PoolTimelineChangesPresenterBase extends PureComponent<PoolTimelineChangesProps, PoolTimelineChangesState> {
    public state: PoolTimelineChangesState = { sort: { key: 'field', order: 'asc' } };

    private get columns(): PaloColumn<PoolTimelineItemChange>[] {
        return [
            {
                key: 'field',
                type: 'name',
                sortKey: 'field',
                head: <FormattedMessage id="app.activity.changes.field" />,
                cell: change => (
                    <FormattedMessage
                        id={'app.activity.changes.field.' + change.propertyName}
                        defaultMessage={change.propertyName}
                    />
                ),
            },
            {
                key: 'old',
                type: 'name',
                head: <FormattedMessage id="app.activity.changes.old" />,
                cell: change => this.parseValue(change.oldValue, change.propertyName) || '--',
            },
            {
                key: 'symbol',
                type: 'icon',
                head: '',
                cell: () => <ChevronRightIcon />,
            },
            {
                key: 'new',
                type: 'name',
                head: <FormattedMessage id="app.activity.changes.new" />,
                cell: change => this.parseValue(change.newValue, change.propertyName) || '--',
            },
        ];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private parseValue(value: any, propertyName: string): any {
        try {
            // simple types
            if (propertyName === 'taxfree') {
                return <FormattedMessage id={`app.activity.changes.${value ? 'true' : 'false'}`} />;
            }

            if (propertyName === 'generatesPurchaseOrder') {
                return <FormattedMessage id={`app.activity.changes.${value ? 'true' : 'false'}`} />;
            }

            if (propertyName === 'isLocked') {
                return <FormattedMessage id={`app.activity.changes.${value ? 'true' : 'false'}`} />;
            }

            if (value === true) {
                return 'true';
            }

            if (value === false) {
                return 'false';
            }

            if (value !== null && typeof value !== 'object') {
                return value;
            }

            // complex types
            if (value.date) {
                const date = dayjs(value.date + value.timezone);
                return date.format('LLL');
            }
            if (propertyName === 'customerContact' && value.id) {
                return <FormattedMessage id="app.activity.changes.newContactId" values={{ value: value.id }} />;
            }
            if (propertyName === 'customerAccount' && value.id) {
                return <FormattedMessage id="app.activity.changes.newCustomerId" values={{ value: value.id }} />;
            }
            if (propertyName === 'avisoContact' && value.id) {
                return <FormattedMessage id="app.activity.changes.newAvisoContactId" values={{ value: value.id }} />;
            }
            if (propertyName === 'supplierAccount' && value.id) {
                return <FormattedMessage id="app.activity.changes.newSupplierId" values={{ value: value.id }} />;
            }
            if (propertyName === 'supplierContact' && value.id) {
                return <FormattedMessage id="app.activity.changes.newContactId" values={{ value: value.id }} />;
            }
            if (propertyName === 'responsibleContact' && value.id) {
                const foundContact = this.props.contacts.find(contact => contact.id === value.id);
                if (foundContact) {
                    return foundContact.fullName;
                } else {
                    return <FormattedMessage id="app.activity.changes.newContactId" values={{ value: value.id }} />;
                }
            }
            if (propertyName === 'deliveryAddress' && value.id) {
                return <FormattedMessage id="app.activity.changes.newDeliveryAddressId" values={{ value: value.id }} />;
            }

            if (typeof value === 'object') {
                return JSON.stringify(value);
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    public render() {
        const { sort } = this.state;
        // The one sortable column is the field name; the entries themselves have
        // no order of their own beyond the one the server sent.
        const changes = [...this.props.changes].sort((a, b) =>
            'asc' === sort.order
                ? a.propertyName.localeCompare(b.propertyName)
                : b.propertyName.localeCompare(a.propertyName)
        );

        return (
            <PaloListView<PoolTimelineItemChange, number>
                embedded={true}
                columns={this.columns}
                items={changes}
                getRowId={change => this.props.changes.indexOf(change)}
                emptyLabel={<FormattedMessage id="app.activity.changes.field" />}
                sort={sort}
                onSortChange={(key, order) => this.setState({ sort: { key, order } })}
            />
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    contacts: state.contacts.flat,
});

export const PoolTimelineChangesPresenter = connect(mapStateToProps, {})(PoolTimelineChangesPresenterBase);
